import React from 'react';
import { CellProps, Column } from 'react-table';

import { TrackableList } from 'types/trackables';

import { pluralize } from 'services/string';
import { relativeTimeFromNow } from 'services/date';

import { useDeleteTrackableLists } from '../../hooks/useDeleteTrackableLists';
import { useModal } from 'hooks/useModal';

import { EmptyView } from 'components/EmptyView/EmptyView';
import { NameCell } from 'components/Table/Table';
import {
  PaginatedTable,
  PaginatedTableProps,
} from 'components/Table/PaginatedTable';

import { TrackableListsTableObjectTypeCell } from './ObjectTypeCell';
import { TrackableListsTableActionsCell } from './ActionsCell';

export type TrackableListsTableProps = Pick<
  PaginatedTableProps<TrackableList>,
  'queryResult' | 'pageSize' | 'page' | 'onPageChange'
> & {
  hasFilters: boolean;
  onCreate: () => void;
  onEdit?: (trackableList: TrackableList) => void;
};

function TrackableListsTable({
  hasFilters,
  onCreate,
  onEdit,
  ...props
}: TrackableListsTableProps) {
  const { open } = useModal();

  const deleteTrackableLists = useDeleteTrackableLists();

  const columns: Column<TrackableList>[] = React.useMemo(
    () => [
      {
        Header: 'Name',
        accessor: 'name',
        Cell: (props: CellProps<TrackableList>) => (
          <NameCell
            {...props}
            entity="trackable list"
            to={`/deploy/trackables/${props.row.original.id}`}
          />
        ),
        sortType: 'string',
        disableGlobalFilter: false,
      },
      {
        Header: 'Object Type',
        accessor: 'object_type',
        Cell: ({
          value,
        }: CellProps<TrackableList, TrackableList['object_type']>) => (
          <TrackableListsTableObjectTypeCell objectType={value} />
        ),
        sortType: 'string',
        disableGlobalFilter: true,
      },
      {
        Header: 'Description',
        accessor: 'description',
        Cell: ({
          value,
        }: CellProps<TrackableList, TrackableList['description']>) => (
          <span className="text-gray-600">{value || '—'}</span>
        ),
        sortType: 'string',
        disableGlobalFilter: false,
      },
      {
        Header: 'Created',
        accessor: 'created_at',
        Cell: ({
          value,
        }: CellProps<TrackableList, TrackableList['created_at']>) => (
          <span className="text-sm text-gray-500">
            {relativeTimeFromNow(value)}
          </span>
        ),
        sortType: 'datetime',
        disableGlobalFilter: true,
      },
      {
        Header: 'Updated',
        accessor: 'updated_at',
        Cell: ({
          value,
        }: CellProps<TrackableList, TrackableList['updated_at']>) => (
          <span className="text-sm text-gray-500">
            {relativeTimeFromNow(value)}
          </span>
        ),
        sortType: 'datetime',
        disableGlobalFilter: true,
      },
      {
        Header: '',
        accessor: 'id',
        Cell: (props: CellProps<TrackableList>) => (
          <TrackableListsTableActionsCell {...props} onEdit={onEdit} />
        ),
        disableSortBy: true,
        disableGlobalFilter: true,
        width: 120,
      },
    ],
    [onEdit]
  );

  const bulkActions = React.useMemo(
    () => [
      {
        intent: 'danger' as const,
        action: 'Delete',
        loading: deleteTrackableLists.isLoading,
        onClick: (ids: string[]) => {
          open('confirm-delete', {
            title: `Are you sure you want to delete ${pluralize(ids.length, 'this', String(ids.length))} ${pluralize(ids.length, 'trackable list')}?`,
            onConfirm: () =>
              deleteTrackableLists.mutate({
                trackable_list_ids: ids,
              }),
          });
        },
      },
    ],
    [open, deleteTrackableLists]
  );

  if (
    !hasFilters &&
    !props.queryResult.isLoading &&
    !props.queryResult.data?.data?.length
  ) {
    return (
      <EmptyView
        title="No watchlists found"
        buttonText="Create watchlist"
        onButtonClick={onCreate}
      >
        Create your first watchlist to start tracking objects in video streams.
      </EmptyView>
    );
  }

  return (
    <PaginatedTable
      {...props}
      id="trackable-lists-table"
      columns={columns}
      bulkActions={bulkActions}
      emptyMessage={
        <EmptyView title="No watchlists found matching your filters.">
          Please try another filter combination or clear filters.
        </EmptyView>
      }
      label="Watchlists"
      onRowNavigate={(trackableList) =>
        `/deploy/trackables/${trackableList.id}`
      }
    />
  );
}

const Memoized = React.memo(TrackableListsTable);

export { Memoized as TrackableListsTable };
